import { useClientResourceGroupQuery } from "@graphql/client-resource-groups/ClientResourceGroups.g";
import {
  useUpdateClientApplicationMutation,
} from "@graphql/client-applications/ClientApplications.g";
import { TenantedClientApplicationReviewStateChoices } from "@graphql/generated/graphql.g";
import { useMemo } from "react";
import { toast } from "react-toastify";

import {
  ClientApplicationApprovalReviewForm,
  ClientApplicationApprovalReviewFormValues,
} from "@components/Forms";

import { useNotification } from "@providers/NotificationProvider/useNotification";

type Props = {
  clientId: string;
  clientApplicationId: string; // This is now actually the resource group ID
};

export const UpdateClientApplicationApprovalForm = ({
  clientId,
  clientApplicationId, // This is now actually the resource group ID
}: Props) => {
  // Fetch the resource group to get the primary application
  const resourceGroupQuery = useClientResourceGroupQuery({
    clientId,
    resourceGroupId: clientApplicationId,
  });
  const updateClientApplication = useUpdateClientApplicationMutation();
  const updateApp = updateClientApplication.mutateAsync;
  const { invalidateNotifications } = useNotification();

  // Use the primary application from the resource group
  const resourceGroupNode = resourceGroupQuery.data?.tenantedResourceGroups?.edges?.[0]?.node;
  const primaryApplicationEdge = resourceGroupNode?.primaryResource?.primaryApplication?.edges?.[0];
  const clientApplication = primaryApplicationEdge?.node ?? undefined;

  const values = useMemo(() => {
    return {
      reviewState:
        clientApplication?.reviewState ??
        TenantedClientApplicationReviewStateChoices.Discovered,
      managedBy: clientApplication?.managedBy,
      supportedBy: clientApplication?.supportedBy,
      businessOwnerId: clientApplication?.businessOwner?.id,
      clientId: clientId,
    };
  }, [clientId, clientApplication]);

  const onSubmit = async (
    values: ClientApplicationApprovalReviewFormValues,
  ) => {
    if (!clientApplication) return;

    try {
      const { updateClientApplication } = await updateApp({
        input: {
          id: clientApplication.id,
          ...values,
        },
      });

      if (updateClientApplication?.errors) {
        updateClientApplication?.errors.forEach((error) => {
          throw Error(error?.message || "Unknown error");
        });
      }

      await resourceGroupQuery.refetch();
      
      // Invalidate notification cache since backend resolves notifications when reviewState changes
      invalidateNotifications();

      toast.success("Application reviewed");
    } catch (error) {
      toast.error("Unable to update application approval state");
      console.error(error);
    }
  };

  return (
    <ClientApplicationApprovalReviewForm
      onSubmit={onSubmit}
      values={values}
      submitText="Update"
    />
  );
};
